import React, { ElementType, FC, useMemo, CSSProperties } from "react";

import { Placement } from "@popperjs/core";
import classnames from "classnames";
import { Moment } from "moment";
import moment from "moment-timezone";

import { SelectDateTarget } from "./SelectDateTarget";
import { StyledSelectDate, StyledSelectDateButton } from "./Styles";
import { DaysToModify } from "./types";
import { formatDate } from "../../utils/dateUtils";
import {
  ChangeMode,
  DatePicker,
  TimestampDateRange,
  toMoment,
} from "../DatePicker/DatePicker";

type TriggerStringType = "text" | "icon" | "inline";
type TriggerFunctionType = (text: string) => JSX.Element;

const isDayInRange = (day: Moment, data: DaysToModify): boolean => {
  const fromMoment = moment.unix(data.from).utc();
  const toMoment = moment.unix(data.to).utc();

  if (data.repeating) {
    const year = day.year();
    const from = moment.utc({
      year,
      month: fromMoment.month(),
      date: fromMoment.date(),
    });
    const to = moment.utc({
      year,
      month: toMoment.month(),
      date: toMoment.date(),
    });

    return day.isSameOrAfter(from, "day") && day.isSameOrBefore(to, "day");
  }

  return (
    day.isSameOrAfter(fromMoment, "day") && day.isSameOrBefore(toMoment, "day")
  );
};

export interface ISelectDate {
  /** Change mode - can be atomic and instant */
  changeMode?: "atomic" | "instant";
  /** Trigger - can be text, icon, inline, or custom render function */
  trigger?: TriggerStringType | TriggerFunctionType;
  /** Save button text */
  saveButtonText?: string;
  /** Cancel button text */
  cancelButtonText?: string;
  /** Clear button text */
  clearButtonText?: string;
  /** Modal title text */
  modalTitle?: string;
  /** Modal description text */
  modalDescription?: string;
  /** Modal save button text */
  modalSaveBtnText?: string;
  /** Modal cancel button text */
  modalCancelBtnText?: string;
  /** Tooltip text */
  tooltipText?: string;
  /** Called when day is clicked */
  onDayClick?: (day: Moment, modifiers: string[]) => void;
  /** Save changes callback */
  onSave?: (dates?: TimestampDateRange) => void;
  /** Changing dates callback */
  onChange?: (dates?: TimestampDateRange) => void;
  /** On cancel closes datepicker or opens discard modal in atomic mode */
  onCancel?: () => void;
  /** Clear dates when they are not required */
  onClear?: () => void;
  /** Use this callback to get is picker visible */
  onToggleDatePicker?: (value: boolean) => void;
  /** Force close */
  forceClose?: boolean;
  /** Set selected day or days */
  selectedDays?: TimestampDateRange;
  /** Set month */
  defaultMonth?: Moment;
  /** Set selection range */
  selectionMode?: ChangeMode;
  /** Set is date required */
  required?: boolean;
  /** Set default show on date picker (only for text and icon mode) */
  defaultShowDatePicker?: boolean;
  /** Long date format (Always show year) */
  longDateFormat?: boolean;
  /** Set first day of week */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** One of icon types */
  icon?: ElementType;
  /** Default Label text */
  defaultLabelText?: string;
  /** Date format */
  dateFormat?: string;
  /** Menu classes */
  menuClassName?: string;
  /** Target element classes */
  targetClassName?: string;
  /** Target element text classes */
  targetTextClassName?: string;
  /** Target element icon classes */
  targetIconClassName?: string;
  /** Background style for clickable element after the menu is open (opacity, color, etc...) */
  backgroundElementClass?: string;
  /** Days to modify */
  daysToModify?: DaysToModify[];
  /** Array of weekend days */
  weekends?: number[];
  /** Weekend label */
  weekendLabel?: string;
  /** Array of nonworking week days */
  nonWorkingDaysOfWeek?: number[];
  /** Nonworking week days label */
  nonWorkingDaysOfWeekLabel?: string;
  /** Allows weekends to be selected */
  weekendIsSelectable?: boolean;
  /** Popper Tooltip style */
  popperTooltipStyle?: CSSProperties;
  /** Popper Tooltip class name*/
  popperTooltipClassName?: string;
  /** Popper class name*/
  popperClassName?: string;
  /** Position of selectDate menu */
  position?: Placement;
  /** Disable days before specified date */
  disableDaysBefore?: Moment;
  /** Disable days after specified date */
  disabledDaysAfter?: Moment;
  /** Enable year selector */
  enableYearPicker?: boolean;
  /** Disable aniamtions */
  disableAnimations?: boolean;
}

export const SelectDate: FC<ISelectDate> = ({
  changeMode: mode = "instant",
  trigger: labelType = "text",
  saveButtonText = "Save",
  cancelButtonText = "Cancel",
  clearButtonText = "Clear",
  modalTitle = "Save Changes",
  modalDescription = "You updated the date. Would you like to save this change?",
  modalSaveBtnText = "Save",
  modalCancelBtnText = "Discard",
  onDayClick,
  onSave,
  onCancel,
  onToggleDatePicker,
  required: dateRequired = false,
  longDateFormat = false,
  defaultShowDatePicker = false,
  firstDayOfWeek = 0,
  selectedDays,
  selectionMode = "custom",
  menuClassName,
  targetClassName,
  icon,
  defaultLabelText = "Set...",
  targetTextClassName,
  targetIconClassName,
  backgroundElementClass,
  dateFormat,
  forceClose,
  defaultMonth,
  daysToModify = [],
  weekends = [],
  weekendLabel = "Weekend",
  nonWorkingDaysOfWeek = [],
  nonWorkingDaysOfWeekLabel = "Unavailable",
  weekendIsSelectable = false,
  tooltipText,
  popperTooltipClassName,
  popperClassName,
  popperTooltipStyle,
  position,
  disableDaysBefore,
  enableYearPicker,
  disableAnimations,
  disabledDaysAfter,
  onChange,
}) => {
  const labelText = useMemo(() => {
    if (!selectedDays) {
      return defaultLabelText;
    } else if (!selectedDays.from && !selectedDays.to) {
      return defaultLabelText;
    } else {
      const formattedEndDate = selectedDays.to
        ? formatDate(toMoment(selectedDays.to), dateFormat, longDateFormat)
        : "";
      const formattedStartDate = selectedDays.from
        ? formatDate(toMoment(selectedDays?.from), dateFormat, longDateFormat)
        : "";

      if (
        selectedDays.to &&
        selectedDays.from &&
        toMoment(selectedDays.from).isSame(toMoment(selectedDays.to))
      ) {
        return formattedEndDate;
      }

      if (!selectedDays.to && selectedDays.from) {
        return formattedStartDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  }, [selectedDays, dateFormat, defaultLabelText, longDateFormat]);

  const modifiers = useMemo(() => {
    const userAvailabilities = daysToModify.filter(
      (data) => data.type === "user_day_off"
    );
    const globalDaysOff = daysToModify.filter(
      (data) => data.type === "global_day_off"
    );
    const selectableGlobalDaysOff = daysToModify.filter(
      (data) => data.type === "selectable_global_day_off"
    );

    return {
      userAvailability: (day: Moment) => {
        const matchedData = userAvailabilities.find((data) =>
          isDayInRange(day, data)
        );
        return {
          matched: !!matchedData,
          title: matchedData ? matchedData.title : null,
        };
      },
      weekend: (day: Moment) => {
        const isWeekend = weekends.includes(day.day());
        return {
          matched: isWeekend,
          title: isWeekend ? weekendLabel : null,
        };
      },
      nonWorkingDay: (day: Moment) => {
        if (weekends.includes(day.day())) {
          return { matched: true, title: "" };
        }
        const nonWorkingDays = [...selectableGlobalDaysOff, ...globalDaysOff];

        const matchedData = nonWorkingDays.find((data) =>
          isDayInRange(day, data)
        );
        return {
          matched: !!matchedData,
          title: matchedData ? matchedData.title : null,
        };
      },
      nonWorkingDaysOfWeek: (day: Moment) => {
        const isNonWorkingDay = nonWorkingDaysOfWeek.includes(day.day());
        return {
          matched: isNonWorkingDay,
          title: isNonWorkingDay ? nonWorkingDaysOfWeekLabel : null,
        };
      },
      day_disabled: (day: Moment) => {
        const globalDayOff = globalDaysOff.find((data) =>
          isDayInRange(day, data)
        );

        return {
          matched: !!globalDayOff,
          title: null,
        };
      },
    };
  }, [
    daysToModify,
    nonWorkingDaysOfWeek,
    nonWorkingDaysOfWeekLabel,
    weekendLabel,
    weekends,
  ]);

  const renderTargetEl = useMemo(() => {
    if (labelType === "icon" && icon) {
      return (
        <SelectDateTarget
          icon={icon}
          title={tooltipText}
          targetIconClassName={targetIconClassName}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
        />
      );
    }
    if (typeof labelType === "function") {
      return labelType(labelText);
    }
    return (
      <StyledSelectDateButton
        type="button"
        className={classnames("date-picker-target", targetClassName)}
      >
        <span className={targetTextClassName}>{labelText}</span>
      </StyledSelectDateButton>
    );
  }, [
    icon,
    labelText,
    labelType,
    popperTooltipClassName,
    popperTooltipStyle,
    targetClassName,
    targetIconClassName,
    targetTextClassName,
    tooltipText,
  ]);

  return (
    <StyledSelectDate className="select-date">
      {labelType !== "inline" ? (
        <DatePicker
          target={renderTargetEl}
          position={position}
          menuClassName={menuClassName}
          popperClassName={popperClassName}
          month={defaultMonth}
          instant={mode === "instant"}
          mode={selectionMode}
          required={dateRequired}
          selected={selectedDays}
          firstDayOfWeek={firstDayOfWeek}
          saveLabel={saveButtonText}
          cancelLabel={cancelButtonText}
          clearLabel={clearButtonText}
          onSave={onSave}
          onChange={onChange}
          onClose={onCancel}
          modifiers={modifiers}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          modalSaveBtnText={modalSaveBtnText}
          modalCancelBtnText={modalCancelBtnText}
          disabledDaysBefore={disableDaysBefore}
          disabledDaysAfter={disabledDaysAfter}
          backgroundElementClass={backgroundElementClass}
          disabled={weekendIsSelectable ? [] : weekends}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
          open={defaultShowDatePicker}
          onCalendarToggle={onToggleDatePicker}
          onDayClick={onDayClick}
          enableConfirmModal={mode === "atomic" && !dateRequired}
          disableYearPicker={!enableYearPicker}
          disableAnimations={disableAnimations}
          showControls
          forceClose={forceClose}
        />
      ) : (
        <DatePicker
          month={defaultMonth}
          instant={mode === "instant"}
          mode={selectionMode}
          required={dateRequired}
          selected={selectedDays}
          firstDayOfWeek={firstDayOfWeek}
          saveLabel={saveButtonText}
          cancelLabel={cancelButtonText}
          clearLabel={clearButtonText}
          disabledDaysBefore={disableDaysBefore}
          disabledDaysAfter={disabledDaysAfter}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
          disabled={weekendIsSelectable ? [] : weekends}
          onSave={onSave}
          onChange={onChange}
          modifiers={modifiers}
          onDayClick={onDayClick}
          disableYearPicker={!enableYearPicker}
          disableAnimations={disableAnimations}
          showControls
          open
        />
      )}
    </StyledSelectDate>
  );
};

SelectDate.displayName = "SelectDate";
